// src/pages/AccountList/index.tsx
import { Flex } from '@chakra-ui/react';
import { useCallback, useState } from 'react';
import { GetParamsObject } from '@/model/query-params';
import { useFiltersForm } from '@/utils/useFiltersForm';
import { PageContent } from 'src/components/PageContent';
import { SectionBox } from 'src/components/SectionBox';
import { AccountListFilters } from './types';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { DateFilterInputValue } from '@/components/DateRangeFilterFields/types';
import { mapDateValueToDateRange } from '@/components/DateRangeFilterFields/utils/mapDateValueToDateRange';
import { useAccount } from '@/services/queries/account';
import { breadcrumb } from './breadcrumb';
import { AccountListFiltersBox } from './components/AccountFilterBox';
import { useNavigate } from 'react-router';
import { AccountTable } from './components/AccountTable/AccountTable';
import { TableListSimpleControls } from '@/components/TableListSimpleControls';
import { useTranslation } from 'react-i18next';

const defaultDateRange = mapDateValueToDateRange(DateFilterInputValue.SEVEN);

export const AccountList = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const filtersForm = useFiltersForm<AccountListFilters>({
    defaultValues: {
      name: '',
      access_type: '',
      start_date: defaultDateRange.startDate,
      end_date: defaultDateRange.endDate,
      dateValue: DateFilterInputValue.SEVEN,
    },
  });

  const filterData = filtersForm.watch();

  const [sorting, setSorting] = useState<{ field: string; desc: boolean }>({ field: 'last_access_at', desc: true });
  const [pagination, setPagination] = useState<{ pageNumber: number; pageSize: number }>({
    pageNumber: 1,
    pageSize: 10,
  });

  const params = GetParamsObject(
    filterData,
    pagination.pageNumber,
    pagination.pageSize,
    sorting.desc ? 'desc' : 'asc',
    sorting.field,
  );

  const { data: itemData, isLoading, forceRefetch } = useAccount(params);

  const itemsData = itemData?.data || [];

  const onHandleEdit = (id: string) => {
    navigate(`/account/${id}`);
  };

  const onHandleDownloadExport = useCallback((type: any) => {
    console.log(type);
  }, []);

  return (
    <PageContent title={t('pages.accountlist.title')} breadcrumbItems={breadcrumb}>
      <SectionBox bodyProps={{ gap: 'small' }}>
        <SectionBox bodyProps={{ gap: 'small' }}>
          <AccountListFiltersBox filtersForm={filtersForm} forceRefetch={forceRefetch} />
        </SectionBox>

        <Flex direction='column' gap='deka' mt={8}>
          <TableListSimpleControls onUpdateList={forceRefetch} onExport={onHandleDownloadExport} />

          <AccountTable
            data={itemsData}
            isLoading={isLoading}
            sorting={sorting}
            pageNumber={pagination.pageNumber}
            pageSize={pagination.pageSize}
            totalPages={itemData?.meta.pages || 0}
            totalItems={itemData?.meta.total || 0}
            onSortingChange={(field, desc) => setSorting({ field, desc })}
            onPaginationChange={(pageNumber, pageSize) => setPagination({ pageNumber, pageSize })}
            onEditClick={item => onHandleEdit(item.uuid)}
          />
        </Flex>
      </SectionBox>

      <CenteredSpinner isLoading={isLoading} />
    </PageContent>
  );
};
